import React from 'react';
import * as d3 from 'd3';
import { Theme } from '../../../theme';

export interface TweetData {
  id: string;
  predictionId?: number; // For share button deep linking
  text: string;
  expert_name: string;
  twitter_handle: string;
  verified: boolean;
  profile_image_url?: string;
  source_url?: string;
  prediction_date?: string;
  // Bet prediction styling
  isBetPrediction?: boolean;
  isWinner?: boolean;
  isLoser?: boolean;
}

export const createTweetVisualization = (
  parent: d3.Selection<SVGGElement, unknown, null, undefined>,
  tweetData: TweetData,
  x: number,
  y: number,
  theme: Theme,
  maxWidth: number = 400, // Dynamic width based on zoom
  fixedHeight?: number, // Optional fixed height for compact mode
  scaleFactor: number = 1.0 // Scale factor for all elements
) => {
  const padding = 16 * scaleFactor;
  const avatarSize = 40 * scaleFactor;
  const contentPadding = 12 * scaleFactor;
  
  // ✅ SMART TRUNCATION: Limit text length to prevent card overflow
  const MAX_CHARACTERS = 580; // Based on example text length
  let displayText = tweetData.text;
  
  if (displayText.length > MAX_CHARACTERS) {
    // Find last complete word before the limit
    const truncated = displayText.substring(0, MAX_CHARACTERS);
    const lastSpaceIndex = truncated.lastIndexOf(' ');
    
    if (lastSpaceIndex > MAX_CHARACTERS * 0.8) { // If we have at least 80% of the text
      displayText = truncated.substring(0, lastSpaceIndex) + '...';
    } else {
      displayText = truncated + '...';
    }
  }

  // ✅ OPTIMIZED: Better space utilization for specific experts
  const isHalFinney = tweetData.expert_name?.toLowerCase().includes('hal finney');
  const isMichaelSaylor = tweetData.expert_name?.toLowerCase().includes('michael saylor');
  const isTuurDemeester = tweetData.expert_name?.toLowerCase().includes('tuur demeester');
  
  const actualPadding = isHalFinney ? padding * 0.7 : padding; // Reduced padding for Hal Finney
  const actualAvatarSize = avatarSize; // Keep normal avatar size for all predictions
  const actualContentPadding = isHalFinney ? contentPadding * 0.6 : contentPadding; // Reduced content padding for Hal
  const contentWidth = maxWidth - actualPadding * 2; // Use reduced padding
  let textAreaWidth = contentWidth - avatarSize - contentPadding;
  
  // ✅ SPECIAL HANDLING FOR BET PREDICTIONS: Use more of the available width
  if (tweetData.isBetPrediction) {
    // For bet predictions, use maximum available width with minimal padding
    textAreaWidth = contentWidth - (avatarSize * 0.7) - (contentPadding * 0.4); // Reduced padding for max width
  }
  
  // For Hal Finney, use more of the available width with reduced spacing
  if (isHalFinney) {
    textAreaWidth = contentWidth - actualAvatarSize - actualContentPadding; // Use optimized sizes
  }
  
  // ✅ FIX: Tuur Demeester optimization - broader card like Hal Finney
  if (isTuurDemeester) {
    textAreaWidth = contentWidth - actualAvatarSize - (contentPadding * 0.8); // More text space for Tuur
  }
  
  let lineHeight = 20 * scaleFactor; // Default line height
  if (isHalFinney) {
    lineHeight = 18 * scaleFactor; // Tighter for Hal Finney
  } else if (isTuurDemeester) {
    lineHeight = 16 * scaleFactor; // Even tighter for Tuur Demeester to make card more compact
  }
  
  const getCharWidth = (char: string) => {
    // Optimized character spacing for different experts
    let spacingMultiplier = 1.0;
    if (isHalFinney) {
      spacingMultiplier = 0.85; // Tighter spacing for Hal Finney
    } else if (isTuurDemeester) {
      spacingMultiplier = 0.9; // Slightly tighter spacing for Tuur Demeester
    }
    
    if (/[0-9]/.test(char)) return 7.0 * spacingMultiplier; // Numbers slightly wider
    if (/[.,;:!?]/.test(char)) return 4.5 * spacingMultiplier; // Punctuation narrower
    if (/[$%@#&]/.test(char)) return 7.5 * spacingMultiplier; // Special symbols
    if (/[MWQG]/.test(char)) return 8.5 * spacingMultiplier; // Wide uppercase letters
    if (/[il1]/.test(char)) return 4.0 * spacingMultiplier; // Narrow letters
    return 6.8 * spacingMultiplier; // Conservative default
  };
  
  const getTextWidth = (text: string) => {
    return Array.from(text).reduce((width, char) => width + getCharWidth(char), 0);
  };
  
  // More accurate line calculation by actually simulating word wrapping
  const wordsArray = displayText.split(' ');
  let currentLineWidth = 0;
  let actualLines = 1;
  
  // Use the same text area width calculation as rendering
  let lineCalcWidth = textAreaWidth * 0.85; // Default
  if (tweetData.isBetPrediction) {
    lineCalcWidth = textAreaWidth * 1.25; // Match the rendering logic - use 125%
  }
  
  for (const word of wordsArray) {
    const wordWidth = getTextWidth(word) + getCharWidth(' '); // Add space width
    if (currentLineWidth + wordWidth > lineCalcWidth && currentLineWidth > 0) { 
      actualLines++;
      currentLineWidth = getTextWidth(word);
    } else {
      currentLineWidth += wordWidth;
    }
  }
  
  const textHeight = Math.max(actualLines * lineHeight, 60);

  // Use fixed height for compact mode, or calculate dynamically with dedicated space for source button
  let sourceButtonSpace = 75; // Default space for source button
  
  // ✅ FIX: Michael Saylor needs more space to prevent text/button overlap
  if (isMichaelSaylor) {
    sourceButtonSpace = 95; // Extra space for Michael Saylor's predictions
  }
  
  const cardHeight = fixedHeight || (actualPadding * 2 + Math.max(avatarSize + 8, textHeight + sourceButtonSpace));
  
  // Debug logging for height calculation
  console.log('Tweet height calculation debug:', {
    textLength: displayText.length,
    actualLines,
    textHeight,
    cardHeight,
    textAreaWidth,
    quote: displayText
  });
  
  // Simple debug for text overflow issues
  if (displayText.length > 150) {
  }

  // Create main card group
  const cardGroup = parent.append('g')
    .attr('class', 'tweet-card')
    .style('cursor', 'default');

  // Remove auto-redirect - let users interact with text without navigation

  // Card background - use fixedHeight if provided, otherwise calculated height
  const actualCardHeight = fixedHeight || cardHeight;
  
  const backgroundRect = cardGroup.append('rect')
    .attr('class', 'tweet-card-bg') // Add class for easy selection
    .attr('x', x)
    .attr('y', y)
    .attr('width', maxWidth)
    .attr('height', actualCardHeight)
    .attr('rx', 4)
    .attr('ry', 4)
    .attr('fill', theme.tweet.backgroundColor)
    .attr('stroke', theme.tweet.borderColor)
    .attr('stroke-width', 1)
    .style('filter', 'drop-shadow(0 1px 3px rgba(0,0,0,0.12))');

  // Variables already declared above for proper ordering
  const avatarX = x + actualPadding; // Use reduced padding for Hal
  const avatarY = y + actualPadding;
  
  // Profile image circle background
  cardGroup.append('circle')
    .attr('cx', avatarX + actualAvatarSize / 2)
    .attr('cy', avatarY + actualAvatarSize / 2)
    .attr('r', actualAvatarSize / 2)
    .attr('fill', '#e1e8ed');

  // Profile image (if available)
  if (tweetData.profile_image_url) {
    cardGroup.append('image')
      .attr('x', avatarX)
      .attr('y', avatarY)
      .attr('width', actualAvatarSize)
      .attr('height', actualAvatarSize)
      .attr('href', tweetData.profile_image_url)
      .attr('clip-path', `circle(${actualAvatarSize/2}px at ${actualAvatarSize/2}px ${actualAvatarSize/2}px)`);
  } else {
    // Default avatar placeholder
    cardGroup.append('text')
      .attr('x', avatarX + actualAvatarSize / 2)
      .attr('y', avatarY + actualAvatarSize / 2 + 6)
      .attr('text-anchor', 'middle')
      .attr('font-family', 'system-ui, -apple-system, sans-serif')
      .attr('font-size', `${18 * scaleFactor}px`)
      .attr('font-weight', 'bold')
      .attr('fill', '#657786')
      .text(tweetData.expert_name.charAt(0).toUpperCase());
  }

  // ✅ OPTIMIZED: Position name and handle based on actual avatar size
  const nameX = x + actualPadding + actualAvatarSize + actualContentPadding; // Use all optimized spacing for Hal
  
  // ✅ BET PREDICTION: Removed black background for name - using theme-appropriate text color instead
  
  // Name
  cardGroup.append('text')
    .attr('x', nameX)
    .attr('y', y + actualPadding + 16 * scaleFactor)
    .attr('font-family', 'system-ui, -apple-system, sans-serif')
    .attr('font-size', `${isHalFinney ? 16 * scaleFactor : 15 * scaleFactor}px`) // Larger name for Hal
    .attr('font-weight', 'bold')
    .attr('fill', tweetData.isBetPrediction ? theme.tweet.nameColor : theme.tweet.nameColor) // Use theme color for both
    .text(tweetData.expert_name);

  // Handle and timestamp on same line
  const handleText = tweetData.twitter_handle.startsWith('@') ? tweetData.twitter_handle : `@${tweetData.twitter_handle}`;
  cardGroup.append('text')
    .attr('x', nameX)
    .attr('y', y + actualPadding + 35 * scaleFactor)
    .attr('font-family', 'system-ui, -apple-system, sans-serif')
    .attr('font-size', `${isHalFinney ? 12 * scaleFactor : 11 * scaleFactor}px`) // Smaller font to prevent crowding
    .attr('font-weight', 'normal')
    .attr('fill', theme.tweet.handleColor)
    .text(handleText);

  // Add timestamp next to handle if available
  if (tweetData.prediction_date) {
    const date = new Date(tweetData.prediction_date);
    const timeStr = ` · ${date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    })}`;
    
    const handleWidth = getTextWidth(handleText) * scaleFactor;
    
    cardGroup.append('text')
      .attr('x', nameX + handleWidth + (4 * scaleFactor)) // Use nameX for consistent alignment
      .attr('y', y + actualPadding + 35 * scaleFactor)
      .attr('font-family', 'system-ui, -apple-system, sans-serif')
      .attr('font-size', `${isHalFinney ? 12 * scaleFactor : 11 * scaleFactor}px`) // Smaller font to prevent crowding
      .attr('font-weight', 'normal')
      .attr('fill', theme.tweet.handleColor)
      .text(timeStr);
  }

  // Tweet text with proper wrapping and GUARANTEED card coverage
  const textStartY = y + actualPadding + (58 * scaleFactor);
  const renderWords = displayText.split(' ');
  let currentLineText = '';
  let lineY = textStartY;
  let currentLinePixelWidth = 0;
  
  // Conservative text area - leave space for source button and margins
  let safeTextAreaWidth = textAreaWidth * 0.85; // Default conservative width
  
  // ✅ BET PREDICTIONS: Use extremely aggressive width for absolute maximum extension
  if (tweetData.isBetPrediction) {
    safeTextAreaWidth = textAreaWidth * 1.25; // Use 125% - extreme extension to card edge
  }
  
  // ✅ OPTIMIZED: Better font sizing for different experts
  let fontSize = 14 * scaleFactor; // Default font size
  if (isHalFinney) {
    fontSize = 15 * scaleFactor; // Larger for Hal Finney's readability
  } else if (isTuurDemeester) {
    fontSize = 12 * scaleFactor; // Even smaller font for Tuur to make card more compact
  }
  
  const textX = nameX; // Use same X position as name/handle for consistent alignment
  
  // Use the SAME algorithm as height calculation for consistency
  for (const word of renderWords) {
    const wordWidth = getTextWidth(word) + getCharWidth(' '); // Add space width
    if (currentLinePixelWidth + wordWidth > safeTextAreaWidth && currentLinePixelWidth > 0) {
      // Add current line with optimized positioning
      cardGroup.append('text')
        .attr('x', textX)
        .attr('y', lineY)
        .attr('font-family', 'system-ui, -apple-system, sans-serif')
        .attr('font-size', `${fontSize}px`)
        .attr('font-weight', 'normal')
        .attr('fill', theme.tweet.textColor)
        .text(currentLineText.trim());
      
      currentLineText = word;
      currentLinePixelWidth = getTextWidth(word);
      lineY += lineHeight;
    } else {
      currentLineText += (currentLineText ? ' ' : '') + word;
      currentLinePixelWidth += wordWidth;
    }
  }

  // Add the last line if there's remaining text
  if (currentLineText) {
    cardGroup.append('text')
      .attr('x', textX)
      .attr('y', lineY)
      .attr('font-family', 'system-ui, -apple-system, sans-serif')
      .attr('font-size', `${fontSize}px`)
      .attr('font-weight', 'normal')
      .attr('fill', theme.tweet.textColor)
      .text(currentLineText.trim());
  }

  // Button positioning - bottom right corner with spacing
  const buttonY = y + actualCardHeight - (15 * scaleFactor);
  const buttonRadius = 8 * scaleFactor;
  let buttonXOffset = 30 * scaleFactor; // Starting offset from right edge

  // Add source button if URL exists
  if (tweetData.source_url) {
    const sourceButtonX = x + maxWidth - buttonXOffset - padding;
    
    const sourceButtonGroup = cardGroup.append('g')
      .attr('class', 'source-button')
      .style('cursor', 'pointer')
      .on('click', (event) => {
        event.stopPropagation();
        window.open(tweetData.source_url!, '_blank');
      });

    // Button background - circular black with Bitcoin orange border
    sourceButtonGroup.append('circle')
      .attr('cx', sourceButtonX)
      .attr('cy', buttonY)
      .attr('r', buttonRadius)
      .attr('fill', '#1a1a1a')
      .attr('stroke', '#F7931A')
      .attr('stroke-width', 1.5 * scaleFactor);

    // Simple arrow pointing right
    const arrowSize = 5 * scaleFactor;
    sourceButtonGroup.append('path')
      .attr('d', `M ${sourceButtonX - arrowSize} ${buttonY} 
                  L ${sourceButtonX + arrowSize} ${buttonY} 
                  M ${sourceButtonX + arrowSize - 3} ${buttonY - 3}
                  L ${sourceButtonX + arrowSize} ${buttonY}
                  L ${sourceButtonX + arrowSize - 3} ${buttonY + 3}`)
      .attr('stroke', '#F7931A')
      .attr('stroke-width', 1.5 * scaleFactor)
      .attr('fill', 'none')
      .attr('stroke-linecap', 'round')
      .attr('stroke-linejoin', 'round');
    
    buttonXOffset += 25 * scaleFactor; // Move next button to the left
  }

  // Add share button if predictionId exists
  if (tweetData.predictionId) {
    const shareButtonX = x + maxWidth - buttonXOffset - padding;
    const shareUrl = `https://proofofprice.com/btc?p=${tweetData.predictionId}`;
    
    const shareButtonGroup = cardGroup.append('g')
      .attr('class', 'share-button')
      .style('cursor', 'pointer')
      .on('click', (event) => {
        event.stopPropagation();
        // Copy to clipboard
        navigator.clipboard.writeText(shareUrl).then(() => {
          // Visual feedback - briefly highlight the button
          shareButtonGroup.select('circle')
            .transition()
            .duration(150)
            .attr('fill', '#1DA1F2')
            .transition()
            .duration(300)
            .attr('fill', '#1a1a1a');
        }).catch(() => {
          // Fallback: open in new tab
          window.open(shareUrl, '_blank');
        });
      });

    // Button background - circular with theme-aware colors
    const isLightMode = theme.tweet.backgroundColor === '#ffffff' || theme.tweet.textColor === '#14171a';
    const shareButtonBg = isLightMode ? '#f0f0f0' : '#1a1a1a';
    const shareIconColor = isLightMode ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.6)';
    const shareBorderColor = isLightMode ? 'rgba(0, 0, 0, 0.2)' : 'rgba(128, 128, 128, 0.5)';
    
    shareButtonGroup.append('circle')
      .attr('cx', shareButtonX)
      .attr('cy', buttonY)
      .attr('r', buttonRadius)
      .attr('fill', shareButtonBg)
      .attr('stroke', shareBorderColor)
      .attr('stroke-width', 1.5 * scaleFactor);

    // Share icon - three dots connected pattern
    const iconSize = 4 * scaleFactor;
    const dotRadius = 2 * scaleFactor;
    
    // Top-right dot
    shareButtonGroup.append('circle')
      .attr('cx', shareButtonX + iconSize * 0.8)
      .attr('cy', buttonY - iconSize * 0.8)
      .attr('r', dotRadius)
      .attr('fill', shareIconColor);
    // Left-center dot  
    shareButtonGroup.append('circle')
      .attr('cx', shareButtonX - iconSize * 0.5)
      .attr('cy', buttonY)
      .attr('r', dotRadius)
      .attr('fill', shareIconColor);
    // Bottom-right dot
    shareButtonGroup.append('circle')
      .attr('cx', shareButtonX + iconSize * 0.8)
      .attr('cy', buttonY + iconSize * 0.8)
      .attr('r', dotRadius)
      .attr('fill', shareIconColor);
    // Connecting lines
    shareButtonGroup.append('path')
      .attr('d', `M ${shareButtonX - iconSize * 0.3} ${buttonY}
                  L ${shareButtonX + iconSize * 0.6} ${buttonY - iconSize * 0.6}
                  M ${shareButtonX - iconSize * 0.3} ${buttonY}
                  L ${shareButtonX + iconSize * 0.6} ${buttonY + iconSize * 0.6}`)
      .attr('stroke', shareIconColor)
      .attr('stroke-width', 1 * scaleFactor)
      .attr('fill', 'none');
  }

  return { cardGroup, cardHeight: actualCardHeight };
};

// React component version (for JSX rendering)
export interface TweetVisualizationProps {
  tweetData: TweetData;
  x: number;
  y: number;
  maxWidth?: number;
  onClick?: () => void;
  theme?: 'light' | 'dark';
}

const TweetVisualization: React.FC<TweetVisualizationProps> = ({ 
  tweetData, 
  x, 
  y, 
  maxWidth = 400, 
  onClick,
  theme = 'light'
}) => {
  const padding = 16;
  const avatarSize = 40;
  const contentPadding = 12;
  
  // Show the whole quote without truncation
  const displayText = tweetData.text;

  // Calculate dimensions with better text wrapping
  const contentWidth = maxWidth - padding * 2;
  const textAreaWidth = contentWidth - avatarSize - contentPadding;
  const lineHeight = 20;
  const charsPerLine = Math.floor(textAreaWidth / 6.5); // More characters per line (6.5px per char)
  const estimatedLines = Math.ceil(displayText.length / charsPerLine);
  const textHeight = Math.max(estimatedLines * lineHeight, 60);

  const cardHeight = Math.min(
    padding * 2 + Math.max(avatarSize + 8, textHeight + 32), 
    220 // Increased height for longer quotes
  );

  const tweetColors = theme === 'dark' ? {
    backgroundColor: '#192734',
    borderColor: '#38444d',
    nameColor: '#ffffff',
    handleColor: '#8b98a5',
    textColor: '#ffffff'
  } : {
    backgroundColor: '#ffffff',
    borderColor: '#e1e8ed',
    nameColor: '#14171a',
    handleColor: '#657786',
    textColor: '#14171a'
  };

  const handleClick = () => {
    // Remove auto-redirect - let users select text without navigation
    onClick?.();
  };

  const handleSourceClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering card click
    if (tweetData.source_url) {
      window.open(tweetData.source_url, '_blank');
    }
  };

  // Simpler and more reliable character width calculation for React
  const getCharWidthReact = (char: string) => {
    if (/[0-9]/.test(char)) return 7.0; // Numbers slightly wider
    if (/[.,;:!?]/.test(char)) return 4.5; // Punctuation narrower
    if (/[$%@#&]/.test(char)) return 7.5; // Special symbols
    if (/[MWQG]/.test(char)) return 8.5; // Wide uppercase letters
    if (/[il1]/.test(char)) return 4.0; // Narrow letters
    return 6.8; // Conservative default
  };
  
  const getTextWidthReact = (text: string) => {
    return Array.from(text).reduce((width, char) => width + getCharWidthReact(char), 0);
  };
  
  // Create wrapped text lines using SAME algorithm as height calculation
  const createTextLines = (text: string) => {
    const words = text.split(' ');
    const lines: string[] = [];
    let currentLine = '';
    let currentLinePixelWidth = 0;

    for (const word of words) {
      const wordWidth = getTextWidthReact(word) + getCharWidthReact(' '); // Add space width
      if (currentLinePixelWidth + wordWidth > textAreaWidth * 0.92 && currentLinePixelWidth > 0) { // 8% safety margin
        lines.push(currentLine.trim());
        currentLine = word;
        currentLinePixelWidth = getTextWidthReact(word);
      } else {
        currentLine += (currentLine ? ' ' : '') + word;
        currentLinePixelWidth += wordWidth;
      }
    }
    
    if (currentLine) {
      lines.push(currentLine.trim());
    }
    
    return lines; // Show all lines for full quote
  };

  const textLines = createTextLines(displayText);

  return (
    <g 
      className="tweet-card"
      style={{ cursor: 'default' }}
      onClick={handleClick}
    >
      {/* Card background */}
      <rect
        x={x}
        y={y}
        width={maxWidth}
        height={cardHeight}
        rx={16}
        ry={16}
        fill={tweetColors.backgroundColor}
        stroke={tweetColors.borderColor}
        strokeWidth={1}
        style={{ filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.12))' }}
      />

      {/* Profile image background */}
      <circle
        cx={x + padding + avatarSize / 2}
        cy={y + padding + avatarSize / 2}
        r={avatarSize / 2}
        fill="#e1e8ed"
      />

      {/* Profile image or placeholder */}
      {tweetData.profile_image_url ? (
        <image
          x={x + padding}
          y={y + padding}
          width={avatarSize}
          height={avatarSize}
          href={tweetData.profile_image_url}
          clipPath={`circle(${avatarSize/2}px at ${avatarSize/2}px ${avatarSize/2}px)`}
        />
      ) : (
        <text
          x={x + padding + avatarSize / 2}
          y={y + padding + avatarSize / 2 + 6}
          textAnchor="middle"
          fontFamily="system-ui, -apple-system, sans-serif"
          fontSize="18px"
          fontWeight="bold"
          fill="#657786"
        >
          {tweetData.expert_name.charAt(0).toUpperCase()}
        </text>
      )}

      {/* Name */}
      <text
        x={x + padding + avatarSize + contentPadding}
        y={y + padding + 16}
        fontFamily="system-ui, -apple-system, sans-serif"
        fontSize="15px"
        fontWeight="bold"
        fill={tweetColors.nameColor}
      >
        {tweetData.expert_name}
      </text>

      {/* Handle and timestamp on same line */}
      <text
        x={x + padding + avatarSize + contentPadding}
        y={y + padding + 35}
        fontFamily="system-ui, -apple-system, sans-serif"
        fontSize="12px"
        fontWeight="normal"
        fill={tweetColors.handleColor}
      >
        {tweetData.twitter_handle.startsWith('@') ? tweetData.twitter_handle : `@${tweetData.twitter_handle}`}
        {tweetData.prediction_date && (
          <tspan fill={tweetColors.handleColor}>
            {` · ${new Date(tweetData.prediction_date).toLocaleDateString('en-US', { 
              month: 'short', 
              day: 'numeric',
              year: 'numeric'
            })}`}
          </tspan>
        )}
      </text>

      {/* Tweet text lines */}
      {textLines.map((line, index) => (
        <text
          key={index}
          x={x + padding + avatarSize + contentPadding}
          y={y + padding + 58 + (index * lineHeight)}
          fontFamily="system-ui, -apple-system, sans-serif"
          fontSize="15px"
          fontWeight="normal"
          fill={tweetColors.textColor}
        >
          {line}
        </text>
      ))}

      {/* Source button */}
      {tweetData.source_url && (
        <g 
          onClick={handleSourceClick}
          style={{ cursor: 'pointer' }}
          className="source-button"
        >
          {/* Circular black button with Bitcoin orange border */}
          <circle
            cx={x + maxWidth - 20 - padding}
            cy={y + cardHeight - 17 - padding/2}
            r={13}
            fill="#1a1a1a"
            stroke="#F7931A"
            strokeWidth={2}
          />
          
          {/* Simple arrow pointing right */}
          <path
            d={`M ${x + maxWidth - 26 - padding} ${y + cardHeight - 17 - padding/2} 
                L ${x + maxWidth - 16 - padding} ${y + cardHeight - 17 - padding/2} 
                M ${x + maxWidth - 20 - padding} ${y + cardHeight - 21 - padding/2}
                L ${x + maxWidth - 16 - padding} ${y + cardHeight - 17 - padding/2}
                L ${x + maxWidth - 20 - padding} ${y + cardHeight - 13 - padding/2}`}
            stroke="#F7931A"
            strokeWidth={2}
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </g>
      )}

    </g>
  );
};

export default TweetVisualization;